// Convierte el HTML de una sección de la documentación a Markdown, sin librerías.
// Cubre las etiquetas que usan las vistas de docs (newViews/Docs, newViews/APIs,
// MarkdownViewer): títulos, párrafos, listas, enlaces, imágenes, tablas, citas,
// código en línea y bloques de código (pre normales, Prism y los DisplayCode*).
// Lo que no es contenido (botones, selectores, iconos, números de línea) se descarta.

type Ctx = {
  listDepth: number; // nivel de anidamiento de listas (0 = fuera de lista)
};

// Elementos que no son contenido
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'SVG', 'BUTTON', 'SELECT', 'OPTION',
  'INPUT', 'TEXTAREA', 'IFRAME', 'CANVAS', 'VIDEO', 'AUDIO',
]);

// Contenedores que separan bloques
const BLOCK_TAGS = new Set([
  'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'MAIN', 'ASIDE', 'NAV',
  'FIGURE', 'FIGCAPTION', 'DETAILS', 'SUMMARY', 'DL', 'DT', 'DD', 'FORM',
]);

const isSkipped = (el: Element): boolean => {
  if (SKIP_TAGS.has(el.tagName.toUpperCase())) return true;
  const cls = typeof el.className === 'string' ? el.className : '';
  // Botón de copiar de DisplayCode, números de línea de react-syntax-highlighter
  // y el contenido que la portada (/default) tiene oculto fuera de pantalla
  return /\b(sizeClipBoard|linenumber|hiddenContentDocs)\b/.test(cls);
};

// Los bloques de código se guardan aparte mientras se normalizan los espacios
// del resto, para no tocar su contenido
const codeBlocks: string[] = [];
const codePlaceholder = (block: string): string => {
  codeBlocks.push(block);
  return `\n\n\u0000${codeBlocks.length - 1}\u0000\n\n`;
};

const absoluteUrl = (url: string | null): string => {
  if (!url) return '';
  try {
    return new URL(url, window.location.href).href;
  } catch (e) {
    return url;
  }
};

const escapeInline = (text: string): string => text.replace(/([\\`*_])/g, '\\$1');

// Texto de un bloque de código, sin números de línea
const codeText = (el: Element): string => {
  let out = '';
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) out += node.textContent;
    else if (node.nodeType === Node.ELEMENT_NODE && !isSkipped(node as Element)) {
      out += (node as Element).tagName === 'BR' ? '\n' : codeText(node as Element);
    }
  });
  return out;
};

const codeLanguage = (pre: Element): string => {
  const candidates = [pre, pre.querySelector('code')].filter(Boolean) as Element[];
  for (const el of candidates) {
    const match = (typeof el.className === 'string' ? el.className : '').match(/language-([\w+#-]+)/);
    if (match) return match[1];
  }
  return '';
};

const fence = (code: string, lang: string): string => {
  const body = code.replace(/^\s*\n/, '').replace(/\s+$/, '');
  // Si el código contiene ```, se usa una valla más larga
  const ticks = body.includes('```') ? '````' : '```';
  return `${ticks}${lang}\n${body}\n${ticks}`;
};

const convertChildren = (el: Element, ctx: Ctx): string => {
  let out = '';
  el.childNodes.forEach((node) => {
    out += convertNode(node, ctx);
  });
  return out;
};

// Contenido en una sola línea (celdas de tabla, títulos)
const inlineText = (el: Element, ctx: Ctx): string =>
  convertChildren(el, ctx).replace(/\s*\n+\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();

const convertList = (list: Element, ctx: Ctx): string => {
  const ordered = list.tagName === 'OL';
  const start = ordered ? parseInt(list.getAttribute('start') || '1', 10) || 1 : 1;
  const indent = '  '.repeat(ctx.listDepth);
  const lines: string[] = [];
  let index = start;

  Array.from(list.children).forEach((item) => {
    if (item.tagName !== 'LI' || isSkipped(item)) return;
    const marker = ordered ? `${index++}.` : '-';

    // El ítem se trocea en el orden original: texto, sublista, texto...
    // El primer texto va en la línea del marcador; el texto que viene después de
    // una sublista va debajo de ella, sangrado como continuación del ítem
    const parts: { list?: string; text?: string }[] = [];
    let text = '';
    item.childNodes.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].includes((child as Element).tagName)) {
        parts.push({ text });
        parts.push({ list: convertList(child as Element, { listDepth: ctx.listDepth + 1 }) });
        text = '';
      } else {
        text += convertNode(child, { listDepth: ctx.listDepth + 1 });
      }
    });
    parts.push({ text });

    const oneLine = (t: string) => t.replace(/\s*\n+\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
    parts.forEach((part, i) => {
      if (part.list) lines.push(part.list);
      else if (i === 0) lines.push(`${indent}${marker} ${oneLine(part.text || '')}`);
      else if (oneLine(part.text || '')) lines.push(`${indent}  ${oneLine(part.text || '')}`);
    });
  });

  return lines.join('\n');
};

const convertTable = (table: Element, ctx: Ctx): string => {
  const rows = Array.from(table.querySelectorAll('tr')).filter((tr) => tr.closest('table') === table);
  if (!rows.length) return '';

  const cellText = (cell: Element) => inlineText(cell, ctx).replace(/\|/g, '\\|') || ' ';
  const matrix = rows.map((tr) => Array.from(tr.children).filter((c) => ['TH', 'TD'].includes(c.tagName)).map(cellText));
  const width = Math.max(...matrix.map((r) => r.length));
  if (!width) return '';

  const pad = (r: string[]) => r.concat(Array(width - r.length).fill(' '));
  const line = (r: string[]) => `| ${pad(r).join(' | ')} |`;
  const [header, ...body] = matrix;

  return [line(header), `| ${Array(width).fill('---').join(' | ')} |`, ...body.map(line)].join('\n');
};

function convertNode(node: Node, ctx: Ctx): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent || '').replace(/\s+/g, ' ');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as Element;
  if (isSkipped(el)) return '';

  const tag = el.tagName.toUpperCase();
  const cls = typeof el.className === 'string' ? el.className : '';

  // Cabecera de los bloques DisplayCode (título + copiar + selector): solo el título
  if (/\bbackgroundHeader2?\b/.test(cls)) {
    const title = el.firstElementChild ? inlineText(el.firstElementChild, ctx) : '';
    return title ? `\n\n**${escapeInline(title)}**\n\n` : '';
  }

  switch (tag) {
    case 'H1': case 'H2': case 'H3': case 'H4': case 'H5': case 'H6': {
      const text = inlineText(el, ctx);
      return text ? `\n\n${'#'.repeat(Number(tag[1]))} ${text}\n\n` : '';
    }
    case 'P':
      return `\n\n${convertChildren(el, ctx).trim()}\n\n`;
    case 'BR':
      return '\n';
    case 'HR':
      return '\n\n---\n\n';
    case 'STRONG': case 'B': {
      const text = convertChildren(el, ctx).trim();
      return text ? ` **${text}** ` : '';
    }
    case 'EM': case 'I': {
      const text = convertChildren(el, ctx).trim();
      return text ? ` _${text}_ ` : '';
    }
    case 'DEL': case 'S':
      return `~~${convertChildren(el, ctx).trim()}~~`;
    case 'CODE': {
      if (el.closest('pre')) return codeText(el);
      const text = (el.textContent || '').replace(/\s+/g, ' ');
      if (!text.trim()) return '';
      const ticks = text.includes('`') ? '``' : '`';
      return `${ticks}${text}${ticks}`;
    }
    case 'PRE':
      return codePlaceholder(fence(codeText(el), codeLanguage(el)));
    case 'A': {
      const text = convertChildren(el, ctx).trim();
      const href = el.getAttribute('href');
      if (!href || href.startsWith('javascript:')) return text;
      const url = absoluteUrl(href);
      return text ? `[${text}](${url})` : url;
    }
    case 'IMG': {
      const src = absoluteUrl(el.getAttribute('src'));
      return src ? `![${el.getAttribute('alt') || ''}](${src})` : '';
    }
    case 'UL': case 'OL':
      return `\n\n${convertList(el, ctx)}\n\n`;
    case 'LI':
      // <li> fuera de una lista
      return `\n\n- ${inlineText(el, ctx)}\n\n`;
    case 'TABLE':
      return `\n\n${convertTable(el, ctx)}\n\n`;
    case 'BLOCKQUOTE': {
      const inner = normalize(convertChildren(el, ctx));
      return `\n\n${inner.split('\n').map((l) => (l ? `> ${l}` : '>')).join('\n')}\n\n`;
    }
    default:
      if (BLOCK_TAGS.has(tag) || /\bant-typography\b/.test(cls)) {
        return `\n\n${convertChildren(el, ctx)}\n\n`;
      }
      return convertChildren(el, ctx); // span, sup, etiquetas desconocidas: en línea
  }
}

// Espacios y líneas en blanco del texto (no toca los bloques de código)
function normalize(md: string): string {
  return md
    .split('\n')
    // Se juntan espacios repetidos dentro de la línea, pero no la sangría inicial
    // (las listas anidadas la usan); un único espacio inicial suelto sí se quita
    .map((line) => line.replace(/(\S)[ \t]{2,}/g, '$1 ').replace(/^ (?=\S)/, '').replace(/\s+$/, ''))
    .join('\n')
    .replace(/ +([.,;:!?)])/g, '$1') // espacio sobrante que dejan ** y _ antes de un signo
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function htmlToMarkdown(root: Element): string {
  codeBlocks.length = 0;
  const md = normalize(convertNode(root, { listDepth: 0 }));
  // Se devuelven los bloques de código a su sitio
  return md.replace(/\u0000(\d+)\u0000/g, (_, i) => codeBlocks[Number(i)]);
}
